import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import "@styles/reports.scss";
import {
  Box,
  Container,
  Typography,
  Paper,
  Tabs,
  Tab,
  useTheme,
  CircularProgress,
  IconButton,
  Alert,
  Badge,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import PrintIcon from "@mui/icons-material/Print";
import SettingsIcon from "@mui/icons-material/Settings";
import AddIcon from "@mui/icons-material/Add";
import { ViewContainer } from "../../components/view-container";
import { contentMaxWidth } from "../../utils/colors";
import { ReportLayoutModal } from "./shared/report-layout-modal";
import { AddChartModal } from "./shared/add-chart-modal";
import { AddStatCardModal } from "./shared/add-stat-card-modal";
import { useReportLayout } from "@hooks/use-report-layout";
import { generateStatCardId, generateChartId } from "@hooks/use-report-layout/utils";
import { useUpdateReportUserConfig } from "@hooks/use-update/report-user-config";
import { useUserContext } from "@hooks/use-auth-context";
import { useCapabilities } from "@hooks/use-capabilities";
import { useReportUserConfigContext, ReportUserConfigProvider } from "@hooks/use-report-user-config-context";
import { useDeleteReportUserConfig } from "@hooks/use-delete/report-user-config";
import { useDeleteUserGeneratedReport } from "@hooks/use-delete/report-user-generated";
import { useUpdateUserGeneratedReport } from "@hooks/use-update/report-user-generated";
import { useAssetsConfigContext } from "@hooks/use-config-context/asset";
import { useTicketConfigContext } from "@hooks/use-config-context/ticket";
import { useUserConfigContext } from "@hooks/use-config-context/user";
import { useGetFeatures } from "@hooks/use-get/features";
import type { WpFieldConfig } from "@hooks/use-config/types";
import { getFieldInputType } from "@hooks/use-config/utils";
import { underscoreToTitleCase } from "@utils/index";
import { usePrintHandler, useConfigDrivenReport } from "@hooks/use-reports";
import type { ConfigDrivenReportConfig } from "@hooks/use-reports/types";
import { useGetReports } from "@hooks/use-get/reports";
import type { BackendReportConfig } from "@hooks/use-get/reports/types";
import { FilterOptionsProvider, ReportFilterStateProvider, type DynamicFilterState } from "@hooks/use-report-filter";
import { mapDbTableToPostType } from "@hooks/use-reports/shared/data-source-config";
import { getStatCardIcon } from "@utils/get-stat-card-icon";
import { applyUserConfigToReport } from "@utils/apply-user-config-to-report";
import { TableCellInputType } from "@components/data-table/types";
import { ReportFilterPanel } from "./shared/report-filter-panel";
import { ActiveFilterChips } from "./shared/active-filter-chips";
import { StatCardsSection } from "./shared/stat-cards-section";
import { ChartsGridSection } from "./shared/charts-grid-section";
import { ReportSettingsPopover } from "./shared/report-settings-popover";
import { CreateReportWizard } from "./shared/create-report-wizard";
import { EXCLUDED_REPORT_COLUMNS } from "./shared/reporting-constants";
import { useScreenAlert } from "@hooks/use-screen-alert";

interface ReportOption {
  id: string;
  label: string;
  icon: React.ReactNode;
  config: ConfigDrivenReportConfig;
  type?: "admin" | "user_generated";
}

/**
 * Helper function to calculate active filter count from dynamic filter state
 */
function getActiveFilterCount(filterState: DynamicFilterState): number {
  let count = 0;
  
  for (const value of Object.values(filterState)) {
    if (Array.isArray(value) && value.length > 0) {
      count++;
    } else if (typeof value === "object" && value !== null && "start" in value && "end" in value) {
      // Date range field
      if (value.start !== null || value.end !== null) {
        count++;
      }
    }
  }
  
  return count;
}

/**
 * Dynamically generate report options from API-fetched reports.
 * Converts backend report configs into navigation options with icons.
 */
const generateReportOptions = (
  reports: BackendReportConfig[]
): ReportOption[] => {
  return reports.map((config) => {
    // Resolve icon from string name if provided
    let IconComponent: React.ComponentType<any> | null = null;
    if (config.icon && typeof config.icon === "string") {
      IconComponent = getStatCardIcon(config.icon);
    }

    return {
      id: config.id || "",
      label: config.title || "Untitled Report",
      icon: IconComponent ? <IconComponent /> : null,
      config,
      type: config.type,
    };
  });
};

const ACTIVE_TAB_KEY = "report_active_tab";

const normalizeRoleValue = (value: unknown): string => {
  if (typeof value !== "string") return "";
  return value.trim().toLowerCase().replace(/[\s-]+/g, "_");
};

/**
 * Given the user's desired ordering of all reports (orderedReportIds), compute
 * a decimal sort_order for each user-generated report, anchored to the
 * surrounding admin reports' sort_order values.
 *
 * Examples (admin sort_orders are whole numbers like 1.0, 2.0, 3.0):
 *   - User report placed between admin 2.0 and 3.0 → 2.01, 2.02 …
 *   - User report placed before the first admin (1.0) → 0.99, 0.98 …
 *   - User report placed after the last admin (3.0) → 3.01, 3.02 …
 *
 * If admin later changes their sort_order the stored values remain unchanged
 * ("roll with it"). The user can re-save to re-anchor against the new positions.
 */
/**
 * Assigns each user-generated report a sort_order that is the midpoint between
 * its left neighbour and the next admin report to its right (binary bisection).
 *
 * Processing left-to-right means already-computed values feed into the next
 * user report's left anchor, giving convergent subdivision:
 *   admin(2.0), user_A, user_B, admin(3.0)
 *   → user_A = (2.0 + 3.0) / 2 = 2.5
 *   → user_B = (2.5 + 3.0) / 2 = 2.75
 *
 * Edge cases:
 *   - Before any admin: virtual left = 0, right = first admin sort_order
 *   - After last admin:  virtual right = prevSort + 1
 *   - No admins at all:  virtual left = 0, virtual right = 1
 */
const computeUserReportSortOverrides = (
  orderedReportIds: string[],
  reportOptions: ReportOption[]
): Record<string, number> => {
  const byId = new Map(reportOptions.map((r) => [r.id, r]));
  const ordered = orderedReportIds
    .map((id) => byId.get(id))
    .filter((r): r is ReportOption => Boolean(r));

  // Tracks effective sort_order for each position; updated as we compute user overrides.
  const effectiveSort: number[] = ordered.map((r) => r.config.sort_order ?? 0);
  const overrides: Record<string, number> = {};

  for (let i = 0; i < ordered.length; i++) {
    if (ordered[i].type !== "user_generated") continue;

    // Left anchor: previous item's effective sort (0 if this is the first item).
    const leftSort = i > 0 ? effectiveSort[i - 1] : 0;

    // Right anchor: sort_order of the next admin report to the right.
    // Falls back to leftSort + 1 if no admin exists to the right.
    let rightSort = leftSort + 1;
    for (let j = i + 1; j < ordered.length; j++) {
      if (ordered[j].type === "admin") {
        rightSort = ordered[j].config.sort_order ?? rightSort;
        break;
      }
    }

    const midpoint = Math.round(((leftSort + rightSort) / 2) * 1e10) / 1e10;
    effectiveSort[i] = midpoint;
    overrides[ordered[i].id] = midpoint;
  }

  return overrides;
};

interface GeneralReportProps {
  config: ConfigDrivenReportConfig;
  toolbarIcon?: React.ReactNode;
  reportType?: "admin" | "user_generated";
  /** Called after a successful settings save so the tab bar refreshes the title */
  onSettingsRefresh?: () => void;
}

const GeneralReport = ({ config, toolbarIcon, reportType, onSettingsRefresh }: GeneralReportProps) => {
  const [filterAnchor, setFilterAnchor] = useState<HTMLElement | null>(null);
  const [layoutModalOpen, setLayoutModalOpen] = useState(false);
  const [addChartModalOpen, setAddChartModalOpen] = useState(false);
  const [addStatCardModalOpen, setAddStatCardModalOpen] = useState(false);
  const [editingChartId, setEditingChartId] = useState<string | null>(null);
  const [editingChartConfig, setEditingChartConfig] = useState<any>(null);
  const [editingStatCardId, setEditingStatCardId] = useState<string | null>(null);
  const [editingStatCardConfig, setEditingStatCardConfig] = useState<any>(null);
  const [layoutSaveLoading, setLayoutSaveLoading] = useState(false);
  const [layoutSaveError, setLayoutSaveError] = useState<string | null>(null);
  const [confirmResetOpen, setConfirmResetOpen] = useState(false);

  const { can } = useCapabilities();
  const canEditReports = can("edit_reports");
  const { setError: setScreenError } = useScreenAlert();

  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const textColor = isDark ? "#ddd" : "#000";
  const lineColor = theme.palette.divider;

  const { updateUserConfig } = useUpdateReportUserConfig();
  const { deleteUserConfig } = useDeleteReportUserConfig();
  const { updateReport } = useUpdateUserGeneratedReport();
  const { getConfigForReport, invalidate: invalidateUserConfigs } = useReportUserConfigContext();

  // Derive user config directly from the shared cache — no per-report API call.
  const userConfig = getConfigForReport(config.id || "");

  // Derive all filterable columns from the data source config (not just groupBy)
  // so the Report Settings filter-field picker covers the full column pool.
  const { config: assetsFieldConfig } = useAssetsConfigContext();
  const { config: ticketsFieldConfig } = useTicketConfigContext();
  const { config: userFieldConfig, tableColumns: userTableColumns } = useUserConfigContext();
  const availableFilterFields = useMemo(() => {
    const dbTable = config.dataSources?.[0]?.dbTable ?? "Assets";
    if (dbTable === "Users") {
      return Object.entries(userFieldConfig || {})
        .filter(([key, field]) => !EXCLUDED_REPORT_COLUMNS.has(key) && key !== "id" && field.filter)
        .map(([key, field]) => ({
          id: key,
          label: field.name,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }

    const raw = dbTable === "Tickets" ? ticketsFieldConfig : assetsFieldConfig;
    if (!raw || Object.keys(raw).length === 0) return [];
    return Object.entries(raw)
      .filter(([key, field]: [string, WpFieldConfig & { display?: boolean }]) => {
        if (EXCLUDED_REPORT_COLUMNS.has(key)) {
          return false;
        }

        if (field?.display === false || !field?.name) {
          return false;
        }

        const inputType = getFieldInputType(field);
        return ![
          TableCellInputType.Form,
          TableCellInputType.UserInput,
        ].includes(inputType);
      })
      .map(([key, field]: [string, WpFieldConfig]) => ({
        id: key,
        label: field.name || underscoreToTitleCase(key),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetsFieldConfig, ticketsFieldConfig, config.dataSources, userFieldConfig, userTableColumns]);

  const { handlePrint, contentRef: printRef } = usePrintHandler(config.title || "Report");

  // Initialize report layout hook FIRST to get access to additionalStatCards
  const reportLayout = useReportLayout({
    reportId: config.id || "",
    initialCharts: config.charts || [],
    initialStatCards: config.statCards || [],
    userConfig: userConfig?.override_config || null,
  });

  // Merge custom charts from both sources (saved + in-memory).
  // For user-generated reports, added charts are part of config.charts already —
  // override_config.addedCharts is only used for admin reports.
  const mergedCustomCharts = useMemo(() => {
    if (reportType === "user_generated") {
      // In-memory unsaved additions only (before the next persist)
      return reportLayout.additionalCharts.map((c) => c.config);
    }
    const merged: Record<string, any> = {
      ...(userConfig?.override_config?.addedCharts || {}),
    };
    reportLayout.additionalCharts.forEach((chart) => {
      merged[chart.id] = chart.config;
    });
    return Object.values(merged);
  }, [reportType, userConfig?.override_config?.addedCharts, reportLayout.additionalCharts]);

  // Merge custom stat cards from both sources (saved + in-memory).
  // For user-generated reports, added stat cards are part of config.statCards already.
  const mergedCustomStatCards = useMemo(() => {
    if (reportType === "user_generated") {
      const merged: Record<string, any> = {};
      reportLayout.additionalStatCards.forEach((card) => {
        merged[card.id] = card.config;
      });
      return merged;
    }
    const merged = {
      ...(userConfig?.override_config?.addedStatCards || {}),
    };
    reportLayout.additionalStatCards.forEach((card) => {
      merged[card.id] = card.config;
    });
    return merged;
  }, [reportType, userConfig?.override_config?.addedStatCards, reportLayout.additionalStatCards]);

  // Apply user config overrides to the report config (for display only)
  const mergedConfig = useMemo(() => {
    const merged = applyUserConfigToReport(config, userConfig);
    return merged;
  }, [config, userConfig]);

  // For user-generated reports, all non-export-table charts and all stat cards
  // in config are user-owned and should show edit/delete buttons in the layout modal.
  // For admin reports, only reportLayout.additionalCharts/StatCards (from override_config) are editable.
  const modalAdditionalCharts = useMemo(() => {
    if (reportType === "user_generated") {
      return (config.charts || [])
        .filter((c) => c.type !== "export-table")
        .map((c) => ({ id: c.id as string, config: c as any }));
    }
    return reportLayout.additionalCharts;
  }, [reportType, config.charts, reportLayout.additionalCharts]);

  const modalAdditionalStatCards = useMemo(() => {
    if (reportType === "user_generated") {
      return (config.statCards || []).map((c) => ({ id: c.id as string, config: c as any }));
    }
    return reportLayout.additionalStatCards;
  }, [reportType, config.statCards, reportLayout.additionalStatCards]);

  const {
    data: reportData,
    loading,
    error,
    filters,
    available,
    exportDetail,
    exportDetailLoading,
    refetch,
  } = useConfigDrivenReport(config, mergedCustomStatCards, mergedCustomCharts);

  const { charts, statCards } = reportData;

  // Refetch report data when custom stat cards change (for unsaved cards to calculate immediately)
  useEffect(() => {
    refetch();
  }, [mergedCustomStatCards, refetch]);

  // Refetch report data when custom charts change
  useEffect(() => {
    refetch();
  }, [mergedCustomCharts, refetch]);

  const activeFilterCount = useMemo(() => getActiveFilterCount(filters), [filters]);

  const chartDataSource = useMemo<"Assets" | "Tickets" | "Users">(() => {
    const dbTable = (mergedConfig.dataSources?.[0]?.dbTable || config.dataSources?.[0]?.dbTable || "Assets").toLowerCase();
    if (dbTable === "tickets") return "Tickets";
    if (dbTable === "users") return "Users";
    return "Assets";
  }, [config.dataSources, mergedConfig.dataSources]);

  // Build the override_config object used by admin-report save paths.
  // addedCharts/addedStatCards are only stored in override_config for admin reports;
  // for user-generated reports those live in admin_config instead.
  const buildOverrideConfig = ({
    charts,
    statCards,
    updatedStatCardId,
    updatedStatCardConfig,
    updatedChartId,
    updatedChartConfig,
  }: {
    charts: Array<{ id: string; hidden?: boolean }>;
    statCards: Array<{ id: string; hidden?: boolean }>;
    updatedStatCardId?: string;
    updatedStatCardConfig?: any;
    updatedChartId?: string;
    updatedChartConfig?: any;
  }) => {
    const chartsOverrides: Record<string, any> = {};
    charts.forEach((chart, idx) => {
      chartsOverrides[chart.id] = { hidden: chart.hidden, order: idx };
    });

    const statCardsOverrides: Record<string, any> = {};
    statCards.forEach((card, idx) => {
      statCardsOverrides[card.id] = { hidden: card.hidden, order: idx };
    });

    const overrideConfig: any = {};
    if (Object.keys(chartsOverrides).length > 0) overrideConfig.charts = chartsOverrides;
    if (Object.keys(statCardsOverrides).length > 0) overrideConfig.statCards = statCardsOverrides;

    // Only admin reports store added items in override_config
    if (reportType !== "user_generated") {
      const addedStatCardsRecord: Record<string, any> = {};
      reportLayout.additionalStatCards?.forEach((card) => {
        addedStatCardsRecord[card.id] =
          card.id === updatedStatCardId
            ? { ...updatedStatCardConfig, id: updatedStatCardId }
            : card.config;
      });

      const addedChartsRecord: Record<string, any> = {};
      reportLayout.additionalCharts?.forEach((chart) => {
        addedChartsRecord[chart.id] =
          chart.id === updatedChartId
            ? { ...updatedChartConfig, id: updatedChartId }
            : chart.config;
      });

      if (Object.keys(addedStatCardsRecord).length > 0) overrideConfig.addedStatCards = addedStatCardsRecord;
      if (Object.keys(addedChartsRecord).length > 0) overrideConfig.addedCharts = addedChartsRecord;
    }

    return overrideConfig;
  };

  // Build the admin_config for user-generated report saves.
  // Merges the existing data source from config with the current charts + stat cards,
  // optionally splicing in an updated chart or stat card.
  // Returns null (and surfaces an error) instead of ever persisting an empty
  // dataSources — that would permanently break the report (fetchAndTransform's
  // guard throws on every future load). Every call site must check for null
  // and abort its save when it gets one.
  const buildUserReportAdminConfig = ({
    updatedChartId,
    updatedChartConfig,
    updatedStatCardId,
    updatedStatCardConfig,
    removedChartId,
    removedStatCardId,
    addedChart,
    addedStatCard,
  }: {
    updatedChartId?: string;
    updatedChartConfig?: any;
    updatedStatCardId?: string;
    updatedStatCardConfig?: any;
    removedChartId?: string;
    removedStatCardId?: string;
    addedChart?: any;
    addedStatCard?: any;
  } = {}) => {
    const dataSource = config.dataSources?.[0];

    if (!dataSource) {
      // eslint-disable-next-line no-console
      console.error(
        `[Reporting] Refusing to save report "${config.id}" — its data source configuration is missing or invalid.`
      );
      setScreenError(
        `Couldn't save "${config.title || "this report"}" — its data source configuration is missing or invalid. Use "Restructure Report" to fix it.`
      );
      return null;
    }

    // Always preserve the existing filter from config (admin-only setting)
    const dsFilter = dataSource.filter
      ? { column: dataSource.filter.column, values: [...dataSource.filter.values] }
      : undefined;

    let charts = [...(config.charts || [])].map((c) =>
      c.id === updatedChartId ? { ...c, ...updatedChartConfig, id: updatedChartId } : c
    );
    if (removedChartId) charts = charts.filter((c) => c.id !== removedChartId);
    if (addedChart) charts = [addedChart, ...charts];

    let statCards = [...(config.statCards || [])].map((c) =>
      c.id === updatedStatCardId ? { ...c, ...updatedStatCardConfig, id: updatedStatCardId } : c
    );
    if (removedStatCardId) statCards = statCards.filter((c) => c.id !== removedStatCardId);
    if (addedStatCard) statCards = [...statCards, addedStatCard];

    return {
      dataSources: [{
        id: dataSource.id,
        dbTable: dataSource.dbTable,
        groupBy: [...dataSource.groupBy],
        ...(dataSource.sum?.length ? { sum: [...dataSource.sum] } : {}),
        ...(dsFilter ? { filter: dsFilter } : {}),
      }],
      charts,
      statCards,
    };
  };

  // Build payload for the layout save modal.
  // For user-generated reports: charts/statCards ordering + added items go to admin_config.
  // For admin reports: visibility/ordering go to override_config as before.
  // When settingsDraft is provided, the new title is applied to the report and the
  // export-table chart title is updated to match.
  const buildPayloadFromDraftLayout = (
    draftLayout: any,
    settingsDraft?: { title: string; description: string; filterFields: string[] }
  ) => {
    if (reportType === "user_generated") {
      const newTitle = settingsDraft?.title.trim();
      const titleForPayload = newTitle ?? config.title ?? "";
      const adminConfig = newTitle
        ? buildUserReportAdminConfig({
            updatedChartId: "exportTable",
            updatedChartConfig: {
              title: `${newTitle} — Data Table`,
              reportName: newTitle,
              exportFilename: `${newTitle.toLowerCase().replace(/\s+/g, "-")}.csv`,
            },
          })
        : buildUserReportAdminConfig();
      if (!adminConfig) return null;
      return {
        title: titleForPayload,
        ...(settingsDraft?.description.trim() ? { description: settingsDraft.description.trim() } : {}),
        ...(settingsDraft ? { report_filters: settingsDraft.filterFields.length > 0 ? settingsDraft.filterFields : undefined } : {}),
        admin_config: adminConfig,
        override_config: buildOverrideConfig({
          charts: draftLayout.charts,
          statCards: draftLayout.statCards,
        }),
      };
    }
    return {
      override_config: buildOverrideConfig({
        charts: draftLayout.charts,
        statCards: draftLayout.statCards,
      }),
    };
  };

  const handleSaveLayout = async (
    draftLayout: any,
    settingsDraft?: { title: string; description: string; filterFields: string[] }
  ) => {
    setLayoutSaveLoading(true);
    setLayoutSaveError(null);

    try {
      if (reportType === "user_generated") {
        // For user-generated reports: persist charts/statCards to admin_config,
        // and visibility/ordering to override_config.
        const payload = buildPayloadFromDraftLayout(draftLayout, settingsDraft);
        if (!payload) {
          setLayoutSaveError("This report's data source configuration is missing or invalid. Use \"Restructure Report\" to fix it.");
          return;
        }
        const [reportResult, configResult] = await Promise.all([
          updateReport(config.id || "", payload as any),
          updateUserConfig(config.id || "", {
            override_config: buildOverrideConfig({
              charts: draftLayout.charts,
              statCards: draftLayout.statCards,
            }),
          }),
        ]);
        if (reportResult && configResult) {
          await Promise.all([invalidateUserConfigs(), onSettingsRefresh?.()]);
          setLayoutModalOpen(false);
        } else {
          setLayoutSaveError("Failed to save layout changes");
        }
      } else {
        const payload = buildPayloadFromDraftLayout(draftLayout);
        const result = await updateUserConfig(config.id || "", payload);
        if (result) {
          await invalidateUserConfigs();
          setLayoutModalOpen(false);
        } else {
          setLayoutSaveError("Failed to save layout changes");
        }
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : "Failed to save layout";
      setLayoutSaveError(errorMsg);
    } finally {
      setLayoutSaveLoading(false);
    }
  };

  // Handle saving a stat card update (with both modals closing)
  const handleSaveStatCardUpdate = async (cardId: string, statCardConfig: any) => {
    setLayoutSaveLoading(true);
    setLayoutSaveError(null);

    try {
      if (reportType === "user_generated") {
        const adminConfig = buildUserReportAdminConfig({
          updatedStatCardId: cardId,
          updatedStatCardConfig: statCardConfig,
        });
        if (!adminConfig) {
          setLayoutSaveError("This report's data source configuration is missing or invalid. Use \"Restructure Report\" to fix it.");
          return;
        }
        const result = await updateReport(config.id || "", {
          title: config.title ?? "",
          admin_config: adminConfig,
        });
        if (result) {
          await Promise.all([invalidateUserConfigs(), onSettingsRefresh?.()]);
        } else {
          setLayoutSaveError("Failed to save stat card changes");
        }
      } else {
        const overrideConfig = buildOverrideConfig({
          charts: reportLayout.layout.charts,
          statCards: reportLayout.layout.statCards,
          updatedStatCardId: cardId,
          updatedStatCardConfig: statCardConfig,
        });
        const result = await updateUserConfig(config.id || "", { override_config: overrideConfig });
        if (result) {
          reportLayout.updateStatCard(cardId, statCardConfig);
          await invalidateUserConfigs();
        } else {
          setLayoutSaveError("Failed to save stat card changes");
        }
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : "Failed to save stat card";
      setLayoutSaveError(errorMsg);
    } finally {
      setLayoutSaveLoading(false);
    }
  };

  // Check if report has any customizations
  const hasCustomizations = useMemo(() => {
    if (!userConfig?.override_config) {
      return (
        (reportLayout.additionalStatCards && reportLayout.additionalStatCards.length > 0) ||
        (reportLayout.additionalCharts && reportLayout.additionalCharts.length > 0)
      );
    }
    const { charts = {}, statCards = {}, addedStatCards = {}, addedCharts = {} } = userConfig.override_config;
    return (
      Object.keys(charts).length > 0 ||
      Object.keys(statCards).length > 0 ||
      Object.keys(addedStatCards).length > 0 ||
      Object.keys(addedCharts).length > 0
    );
  }, [userConfig, reportLayout.additionalStatCards, reportLayout.additionalCharts]);

  // Reset report to admin settings
  const handleResetLayout = async () => {
    setConfirmResetOpen(false);
    setLayoutSaveLoading(true);

    try {
      const result = await deleteUserConfig(config.id || "");

      // API returns the deleted record on success: { id: string, user_id: number }
      // On error, deleteUserConfig returns null
      if (result && result.id) {
        // Hard refresh to regenerate everything from scratch
        window.location.reload();
      } else {
        setLayoutSaveError("Failed to reset layout");
        setLayoutSaveLoading(false);
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : "Failed to reset layout";
      setLayoutSaveError(errorMsg);
      setLayoutSaveLoading(false);
    }
  };

  // Handle deletion of added stat cards
  const handleDeleteAddedStatCard = async (cardId: string) => {
    if (reportType === "user_generated") {
      const adminConfig = buildUserReportAdminConfig({ removedStatCardId: cardId });
      if (!adminConfig) return;
      const result = await updateReport(config.id || "", {
        title: config.title ?? "",
        admin_config: adminConfig,
      });
      if (result) {
        await Promise.all([invalidateUserConfigs(), onSettingsRefresh?.()]);
      }
    } else {
      reportLayout.removeStatCard(cardId);
    }
  };

  // Handle editing of added stat cards
  const handleEditAddedStatCard = (cardId: string, config: any) => {
    setEditingStatCardId(cardId);
    setEditingStatCardConfig(config);
    setAddStatCardModalOpen(true);
  };

  // Handle deletion of added charts
  const handleDeleteAddedChart = async (chartId: string) => {
    if (reportType === "user_generated") {
      const adminConfig = buildUserReportAdminConfig({ removedChartId: chartId });
      if (!adminConfig) return;
      const result = await updateReport(config.id || "", {
        title: config.title ?? "",
        admin_config: adminConfig,
      });
      if (result) {
        await Promise.all([invalidateUserConfigs(), onSettingsRefresh?.()]);
      }
    } else {
      reportLayout.removeChart(chartId);
    }
  };

  // Handle editing of added charts
  const handleEditAddedChart = (chartId: string, chartConfig: any) => {
    setEditingChartId(chartId);
    setEditingChartConfig(chartConfig);
    setAddChartModalOpen(true);
  };

  // Handle saving a chart update (closes both modals)
  const handleSaveChartUpdate = async (chartId: string, chartConfig: any) => {
    setLayoutSaveLoading(true);
    setLayoutSaveError(null);

    try {
      if (reportType === "user_generated") {
        const adminConfig = buildUserReportAdminConfig({
          updatedChartId: chartId,
          updatedChartConfig: chartConfig,
        });
        if (!adminConfig) {
          setLayoutSaveError("This report's data source configuration is missing or invalid. Use \"Restructure Report\" to fix it.");
          return;
        }
        const result = await updateReport(config.id || "", {
          title: config.title ?? "",
          admin_config: adminConfig,
        });
        if (result) {
          await Promise.all([invalidateUserConfigs(), onSettingsRefresh?.()]);
        } else {
          setLayoutSaveError("Failed to save chart changes");
        }
      } else {
        const overrideConfig = buildOverrideConfig({
          charts: reportLayout.layout.charts,
          statCards: reportLayout.layout.statCards,
          updatedChartId: chartId,
          updatedChartConfig: chartConfig,
        });
        const result = await updateUserConfig(config.id || "", { override_config: overrideConfig });
        if (result) {
          reportLayout.updateChart(chartId, chartConfig);
          await invalidateUserConfigs();
        } else {
          setLayoutSaveError("Failed to save chart changes");
        }
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : "Failed to save chart";
      setLayoutSaveError(errorMsg);
    } finally {
      setLayoutSaveLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }} className="no-print">
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {toolbarIcon && <Box sx={{ display: "flex", alignItems: "center" }}>{toolbarIcon}</Box>}
          <Typography variant="h5">{config.title}</Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <IconButton
            onClick={() => handlePrint()}
            sx={{ border: 1, borderColor: "divider" }}
            title="Print Report"
          >
            <PrintIcon />
          </IconButton>
          <IconButton
            onClick={(e) => setFilterAnchor(e.currentTarget)}
            color={filterAnchor ? "primary" : "default"}
            sx={{ border: 1, borderColor: "divider" }}
          >
            <Badge badgeContent={activeFilterCount} color="error" invisible={activeFilterCount === 0}>
              <FilterListIcon />
            </Badge>
          </IconButton>
          {canEditReports && (
          <IconButton
            onClick={() => setLayoutModalOpen(true)}
            sx={{ border: 1, borderColor: "divider" }}
            title="Edit Report Layout"
          >
            <Badge
              badgeContent={hasCustomizations ? <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "primary.main" }} /> : 0}
              overlap="circular"
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <SettingsIcon />
            </Badge>
          </IconButton>
          )}
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography variant="body2" color="textSecondary">Loading report data...</Typography>
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          <strong>Failed to load report data:</strong> {error}
        </Alert>
      ) : (
        <Box>
          <Typography variant="body2" color="textSecondary" sx={{ mb: activeFilterCount > 0 ? 1 : 3 }}>
            {config.description}
          </Typography>

          <ReportFilterPanel
            open={Boolean(filterAnchor)}
            anchorEl={filterAnchor}
            onClose={() => setFilterAnchor(null)}
            available={available}
            enabledFields={config.report_filters}
          />

          <div ref={printRef} className="report-view">
            <Box sx={{ display: "none", "@media print": { display: "block", mb: 2 } }}>
              <Typography variant="h5" sx={{ mb: 1 }}>{config.title}</Typography>
              <Typography variant="body2" color="textSecondary">Generated on {new Date().toLocaleDateString()}</Typography>
            </Box>

            <ActiveFilterChips enabledFields={config.report_filters} />

            {/* Empty-state prompt when the only chart is the auto-generated export table */}
            {(mergedConfig.charts || []).every((c) => c.type === "export-table") &&
              (mergedConfig.statCards || []).length === 0 &&
              mergedCustomCharts.filter((c: any) => c.type !== "export-table").length === 0 &&
              Object.keys(mergedCustomStatCards).length === 0 && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    py: 8,
                    gap: 1.5,
                  }}
                >
                  <Typography variant="h6">Your report is empty</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    It looks like there are no charts or stat cards yet. Open the settings to add some.
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<SettingsIcon />}
                    onClick={() => setLayoutModalOpen(true)}
                    sx={{ display: canEditReports ? undefined : "none" }}
                  >
                    Add Charts &amp; Stat Cards
                  </Button>
                </Box>
              )}

            <StatCardsSection statCardsData={statCards} statCardDefinitions={mergedConfig.statCards || []} />

            <ChartsGridSection
              charts={mergedConfig.charts || []}
              chartData={charts}
              textColor={textColor}
              lineColor={lineColor}
              filters={filters}
              dataSource={chartDataSource}
              onDetailExport={exportDetail}
              detailExportLoading={exportDetailLoading}
            />

            {/* Layout Editor Modal */}
            <ReportLayoutModal
              open={layoutModalOpen}
              onClose={() => setLayoutModalOpen(false)}
              allCharts={config.charts || []}
              allStatCards={config.statCards || []}
              currentLayout={reportLayout.layout}
              onSave={handleSaveLayout}
              onReset={() => setConfirmResetOpen(true)}
              onOpenAddChartModal={() => setAddChartModalOpen(true)}
              onOpenAddStatCardModal={() => {
                setEditingStatCardId(null);
                setEditingStatCardConfig(null);
                setAddStatCardModalOpen(true);
              }}
              isSaving={layoutSaveLoading}
              saveError={layoutSaveError}
              hasCustomizations={hasCustomizations && reportType !== "user_generated"}
              additionalStatCards={modalAdditionalStatCards}
              onDeleteAddedStatCard={handleDeleteAddedStatCard}
              onEditAddedStatCard={handleEditAddedStatCard}
              additionalCharts={modalAdditionalCharts}
              onDeleteAddedChart={handleDeleteAddedChart}
              onEditAddedChart={handleEditAddedChart}
              {...(reportType === "user_generated" && {
                reportSettings: {
                  title: config.title ?? "",
                  description: (config as any).description ?? "",
                  filterFields: [...(config.report_filters ?? [])],
                  availableFilterFields,
                  dataSource: (config.dataSources?.[0]?.dbTable ?? "Assets") as "Assets" | "Tickets" | "Users",
                },
              })}
            />

            {/* Add Chart Modal */}
            <AddChartModal
              open={addChartModalOpen}
              onClose={() => {
                setAddChartModalOpen(false);
                setEditingChartId(null);
                setEditingChartConfig(null);
              }}
              reportConfig={config}
              editingChartId={editingChartId}
              editingConfig={editingChartConfig}
              onConfirm={async (chartConfig) => {
                if (reportType === "user_generated") {
                  const chartWithId = { ...chartConfig, id: chartConfig.id || generateChartId() };
                  const adminConfig = buildUserReportAdminConfig({ addedChart: chartWithId });
                  if (!adminConfig) return;
                  const result = await updateReport(config.id || "", {
                    title: config.title ?? "",
                    admin_config: adminConfig,
                  });
                  if (result) {
                    await Promise.all([invalidateUserConfigs(), onSettingsRefresh?.()]);
                  }
                } else {
                  reportLayout.addChart(chartConfig);
                }
                setAddChartModalOpen(false);
                setEditingChartId(null);
                setEditingChartConfig(null);
              }}
              onUpdate={async (chartId, chartConfig) => {
                await handleSaveChartUpdate(chartId, chartConfig);
                setAddChartModalOpen(false);
                setLayoutModalOpen(false);
                setEditingChartId(null);
                setEditingChartConfig(null);
              }}
            />

            {/* Add Stat Card Modal */}
            <AddStatCardModal
              open={addStatCardModalOpen}
              onClose={() => {
                setAddStatCardModalOpen(false);
                setEditingStatCardId(null);
                setEditingStatCardConfig(null);
              }}
              reportConfig={config}
              editingCardId={editingStatCardId}
              editingConfig={editingStatCardConfig}
              onConfirm={async (statCardConfig) => {
                if (reportType === "user_generated") {
                  const cardWithId = { ...statCardConfig, id: statCardConfig.id || generateStatCardId() };
                  const adminConfig = buildUserReportAdminConfig({ addedStatCard: cardWithId });
                  if (!adminConfig) return;
                  const result = await updateReport(config.id || "", {
                    title: config.title ?? "",
                    admin_config: adminConfig,
                  });
                  if (result) {
                    await Promise.all([invalidateUserConfigs(), onSettingsRefresh?.()]);
                  }
                } else {
                  // Add the custom stat card to the layout
                  reportLayout.addStatCard(statCardConfig);
                }
                setAddStatCardModalOpen(false);
                setEditingStatCardId(null);
                setEditingStatCardConfig(null);
              }}
              onUpdate={async (cardId, statCardConfig) => {
                // Save the stat card update and close both modals
                await handleSaveStatCardUpdate(cardId, statCardConfig);
                
                // Close both modals after save completes
                setAddStatCardModalOpen(false);
                setLayoutModalOpen(false);
                setEditingStatCardId(null);
                setEditingStatCardConfig(null);
              }}
            />
          </div>
        </Box>
      )}

      {/* Reset to Admin Settings confirmation dialog */}
      <Dialog open={confirmResetOpen} onClose={() => setConfirmResetOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Reset to Admin Settings?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will remove all your customizations and restore the report to its default admin configuration. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmResetOpen(false)} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleResetLayout} variant="contained" color="warning">
            Reset
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const ReportingViewInner = () => {
  const theme = useTheme();
  const { user } = useUserContext();
  const { can } = useCapabilities();
  const canCreateReports = can("create_reports");
  const canEditReports = can("edit_reports");
  
  // Fetch reports from backend API
  const { reports, loading, error, getReports } = useGetReports();
  const { features } = useGetFeatures();

  // --- Global report settings (show/hide reports) ---
  const [settingsAnchor, setSettingsAnchor] = useState<HTMLElement | null>(null);
  const [hiddenReports, setHiddenReports] = useState<string[]>([]);
  const [savingSettings, setSavingSettings] = useState(false);
  const [createWizardOpen, setCreateWizardOpen] = useState(false);
  const {
    allUserConfigs,
    loading: userConfigsLoading,
    invalidate: invalidateUserConfigs,
  } = useReportUserConfigContext();

  // Delete state for user-generated reports
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  // Restructure state — opening the wizard in edit mode after a destructive confirm
  const [restructureId, setRestructureId] = useState<string | null>(null);
  const [restructureConfirmOpen, setRestructureConfirmOpen] = useState(false);

  // When a new report is created, store its ID here so the visibleReportOptions
  // effect can navigate to it once the report list has refreshed.
  const pendingSelectRef = useRef<string | null>(null);

  // Generate report options from API data, sorted by sort_order.
  // sort_order comes from the backend directly (including user-set values after a save + refresh).
  const reportOptions = useMemo(() => {
    const options = generateReportOptions(reports);
    return [...options].sort((a, b) => (a.config.sort_order ?? 0) - (b.config.sort_order ?? 0));
  }, [reports]);

  const { updateUserConfig } = useUpdateReportUserConfig();
  const { updateReport: updateUserGeneratedReport } = useUpdateUserGeneratedReport();
  const { deleteReport: deleteUserGeneratedReport, loading: deletingReport } = useDeleteUserGeneratedReport();

  // Sync hidden reports whenever the cached user configs update.
  useEffect(() => {
    const hidden = allUserConfigs
      .filter((c) => c.is_active === 0 || c.is_active === "0" || c.is_active === false)
      .map((c) => String(c.report_id || c.id));
    setHiddenReports(hidden);
  }, [allUserConfigs]);

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmId) return;
    const result = await deleteUserGeneratedReport(deleteConfirmId);
    if (result) {
      setDeleteConfirmId(null);
      // If the deleted report is currently selected, the tab will auto-select the first visible one
      getReports();
    }
  };

  const handleSaveSettings = async (payload: { hiddenReports: string[]; orderedReportIds: string[] }) => {
    setSavingSettings(true);

    try {
      const { hiddenReports: newHidden, orderedReportIds } = payload;
      const toHide = newHidden.filter((id) => !hiddenReports.includes(id));
      const toShow = hiddenReports.filter((id) => !newHidden.includes(id));
      const configsByReportId = Object.fromEntries(
        allUserConfigs.map((c) => [c.report_id || c.id, c])
      );

      // Persist visibility changes via the user-config endpoint (1 call per changed report).
      const visibilityChanges = [...toHide, ...toShow];
      const visibilitySaves = visibilityChanges.map((reportId) => {
        const existing = configsByReportId[reportId];
        return updateUserConfig(reportId, {
          is_active: toHide.includes(reportId) ? 0 : 1,
          override_config: existing?.override_config ?? {},
        });
      });

      // Persist sort order overrides via PUT /user-generated for user reports.
      // Only save reports whose sort_order actually changed to minimise API calls.
      const sortOverrides = computeUserReportSortOverrides(orderedReportIds, reportOptions);
      const sortSaves = Object.entries(sortOverrides)
        .filter(([reportId, sort_order]) => {
          const current = reportOptions.find((opt) => opt.id === reportId)?.config.sort_order;
          return current === undefined || Math.abs((current ?? 0) - sort_order) > 1e-9;
        })
        .map(([reportId, sort_order]) => {
          const report = reportOptions.find((opt) => opt.id === reportId);
          if (!report) return Promise.resolve(null);
          return updateUserGeneratedReport(reportId, {
            title: report.label,
            sort_order,
          });
        });

      await Promise.all([...visibilitySaves, ...sortSaves]);

      setHiddenReports(newHidden);
      setSettingsAnchor(null);
      // Refresh reports list so updated sort_order values are reflected in tab order.
      await Promise.all([invalidateUserConfigs(), getReports()]);
    } finally {
      setSavingSettings(false);
    }
  };

  // Visible reports = those the user hasn't hidden AND that their role permits.
  // User-generated reports are always visible (no permission gate).
  // Admin reports with no permissions field are visible to all.
  // Admin reports with a permissions.roles array are only visible to matching roles.
  const visibleReportOptions = useMemo(() => {
    const roleCandidates = new Set<string>();
    const addRoleCandidate = (value: unknown) => {
      const normalized = normalizeRoleValue(value);
      if (normalized) roleCandidates.add(normalized);
    };

    addRoleCandidate(user?.role);

    // Defensive fallback in case auth payload shape changes and role isn't flattened.
    const nestedRoleNodes = (user as any)?.roles?.nodes;
    if (Array.isArray(nestedRoleNodes)) {
      nestedRoleNodes.forEach((node: any) => {
        addRoleCandidate(node?.slug);
        addRoleCandidate(node?.name);
      });
    }

    const normalizedUserRoles = Array.from(roleCandidates);

    const filtered = reportOptions.filter((opt) => {
      if (hiddenReports.includes(opt.id)) return false;
      // Hide user-sourced reports when the user extension is disabled
      if (!features.user_extension && opt.config.dataSources?.[0]?.dbTable?.toLowerCase() === "users") return false;
      if (opt.type === "user_generated") return true;
      const allowedRoles = opt.config.permissions?.roles;
      if (!allowedRoles || allowedRoles.length === 0) return true;

      const normalizedAllowedRoles = allowedRoles
        .map((role) => normalizeRoleValue(role))
        .filter(Boolean);

      // If role data is unavailable or changed shape, fail open for UI visibility.
      // Server endpoints still enforce actual authorization for data access.
      if (normalizedUserRoles.length === 0) return true;

      return normalizedAllowedRoles.some((allowedRole) =>
        normalizedUserRoles.includes(allowedRole),
      );
    });

    // Prevent a dead-end state where reports exist from API but all are filtered out
    // due to stale role shape or cross-user visibility config leakage.
    if (filtered.length === 0 && reportOptions.length > 0) {
      return reportOptions;
    }

    return filtered;
  }, [reportOptions, hiddenReports, user, features.user_extension]);
  // ---

  // Initialize selected report from sessionStorage or use first available
  const [selectedReport, setSelectedReport] = useState<string>(
    () => {
      const saved = sessionStorage.getItem(ACTIVE_TAB_KEY);
      // Return saved value or let useEffect handle it
      return saved || "";
    }
  );

  // Fetch reports on mount. Guarded with a ref because `getReports` gets a new
  // identity every time the auth token refreshes (it closes over `user?.authToken`)
  // — without the guard this effect would silently re-fetch (and reset) the
  // entire reports list on every background token refresh. Also gated on
  // `user?.authToken` so the ref isn't consumed before the auth provider has
  // restored `user` from storage — otherwise this would fire once with an
  // empty token/refresh-token and never retry once `user` becomes available.
  const hasFetchedReports = useRef(false);
  // Shared by the mount effect and the error screen's Retry button so both
  // claim/un-claim the same guard — a retry that isn't routed through this
  // would leave the ref permanently `false` after a successful retry, letting
  // the next token refresh re-fetch (and reset) the reports list again.
  const fetchReportsGuarded = useCallback(() => {
    hasFetchedReports.current = true;
    getReports().then((result) => {
      if (result === null) {
        hasFetchedReports.current = false;
      }
    });
  }, [getReports]);

  useEffect(() => {
    if (hasFetchedReports.current || !user?.authToken) return;
    fetchReportsGuarded();
  }, [fetchReportsGuarded, user?.authToken]);

  // Update selectedReport when reports are loaded (prefer visible reports)
  useEffect(() => {
    if (visibleReportOptions.length > 0) {
      // If a new report was just created, navigate to it
      if (pendingSelectRef.current) {
        const newId = pendingSelectRef.current;
        pendingSelectRef.current = null;
        const newReport = visibleReportOptions.find((r) => r.id === newId);
        if (newReport) {
          setSelectedReport(newId);
          sessionStorage.setItem(ACTIVE_TAB_KEY, newId);
          return;
        }
      }

      const saved = sessionStorage.getItem(ACTIVE_TAB_KEY);
      const validReport = visibleReportOptions.find(r => r.id === saved);
      
      if (validReport) {
        setSelectedReport(validReport.id || "");
      } else {
        // Use first visible report if saved one is hidden or doesn't exist
        const firstReportId = visibleReportOptions[0]?.id || "";
        setSelectedReport(firstReportId);
        sessionStorage.setItem(ACTIVE_TAB_KEY, firstReportId);
      }
    }
  }, [visibleReportOptions]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    sessionStorage.setItem(ACTIVE_TAB_KEY, newValue);
    setSelectedReport(newValue);
  };

  const getReportOption = (id: string) => visibleReportOptions.find(opt => opt.id === id);

  const currentReportOption = getReportOption(selectedReport);
  const currentReportFilters = currentReportOption?.config.report_filters;

  // Union of every report's filter fields — fetched once on load rather than
  // re-fetched on every tab switch.
  const { allReportFilterFields, filterFieldPostTypeMap } = useMemo(() => {
    const fields = new Set<string>();
    const postTypeMap: Record<string, 'asset' | 'ticket' | 'user'> = {};
    visibleReportOptions.forEach((opt) => {
      const dbTable = opt.config.dataSources?.[0]?.dbTable;
      const postType = mapDbTableToPostType(dbTable);
      opt.config.report_filters?.forEach((f) => {
        fields.add(f);
        postTypeMap[f] = postType;
      });
    });
    return { allReportFilterFields: [...fields], filterFieldPostTypeMap: postTypeMap };
  }, [visibleReportOptions]);

  const renderReportContent = () => {
    if (!currentReportOption) return null;
    
    return <GeneralReport config={currentReportOption.config} toolbarIcon={currentReportOption.icon} reportType={currentReportOption.type} onSettingsRefresh={getReports} />;
  };

  // Show loading state while fetching reports or user configs
  if (loading || userConfigsLoading) {
    return (
      <ViewContainer className="reporting-view">
        <Container maxWidth={contentMaxWidth.data} sx={{ py: 3 }}>
          <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
            Reporting Insights
          </Typography>

          <Paper sx={{ p: 6, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="body2" color="textSecondary">Loading reports...</Typography>
          </Paper>
        </Container>
      </ViewContainer>
    );
  }

  // Show error state if fetch failed
  if (error) {
    return (
      <ViewContainer className="reporting-view">
        <Container maxWidth={contentMaxWidth.data} sx={{ py: 3 }}>
          <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
            Reporting Insights
          </Typography>
          <Paper sx={{ p: 3 }}>
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={() => fetchReportsGuarded()}>
                  Retry
                </Button>
              }
            >
              <strong>Failed to load reports:</strong> {error}
            </Alert>
          </Paper>
        </Container>
      </ViewContainer>
    );
  }

  // Show message if no reports are available
  if (visibleReportOptions.length === 0) {
    return (
      <ViewContainer className="reporting-view">
        <Container maxWidth={contentMaxWidth.data} sx={{ py: 3 }}>
          <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
            Reporting Insights
          </Typography>
          <Paper sx={{ p: 3 }}>
            <Alert severity="info">
              No reports are currently available. Check back later or contact your administrator.
            </Alert>
          </Paper>
        </Container>
      </ViewContainer>
    );
  }

  return (
    <FilterOptionsProvider enabledFields={allReportFilterFields} fieldPostTypeMap={filterFieldPostTypeMap}>
    <ReportFilterStateProvider key={selectedReport} reportId={selectedReport} enabledFields={currentReportFilters}>
    <ViewContainer className="reporting-view">
      <Container maxWidth={contentMaxWidth.data} sx={{ py: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
          <Typography variant="h4" component="h1">
            Reporting Insights
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {canCreateReports && (
              <IconButton
                onClick={() => {
                  setSettingsAnchor(null);
                  setCreateWizardOpen(true);
                }}
                title="Create new report"
                sx={{ border: 1, borderColor: "grey.400", color: "grey.700" }}
              >
                <AddIcon />
              </IconButton>
            )}
            {canEditReports && (
              <IconButton
                onClick={(e) => setSettingsAnchor(e.currentTarget)}
                title="Report settings"
                sx={{ border: 1, borderColor: "divider" }}
              >
                <SettingsIcon />
              </IconButton>
            )}
          </Box>
        </Box>

        <ReportSettingsPopover
          anchor={settingsAnchor}
          onClose={() => setSettingsAnchor(null)}
          reports={reportOptions.map((opt) => ({ id: opt.id, title: opt.label, type: opt.type }))}
          hiddenReports={hiddenReports}
          onSave={handleSaveSettings}
          saving={savingSettings}
          onCreateNew={canCreateReports ? () => {
            setSettingsAnchor(null);
            setCreateWizardOpen(true);
          } : undefined}
          onDeleteReport={(id) => setDeleteConfirmId(id)}
          onRestructureReport={(id) => { setRestructureId(id); setRestructureConfirmOpen(true); }}
        />

        {createWizardOpen && (
          <CreateReportWizard
            open
            userExtensionEnabled={features.user_extension}
            onClose={() => setCreateWizardOpen(false)}
            onCreated={(newReportId: string) => {
              pendingSelectRef.current = newReportId;
              getReports();
            }}
          />
        )}

        {/* Restructure report wizard — opened after destructive confirm */}
        {restructureId && (() => {
          const opt = reportOptions.find((r) => r.id === restructureId);
          if (!opt) return null;
          const src = opt.config.dataSources?.[0];
          return (
            <CreateReportWizard
              open={!!restructureId && !restructureConfirmOpen}
              userExtensionEnabled={features.user_extension}
              onClose={() => setRestructureId(null)}
              initialData={{
                reportId: restructureId,
                title: opt.label,
                description: (opt.config as any).description ?? "",
                dataSource: (src?.dbTable as "Assets" | "Tickets" | "Users") ?? "Assets",
                groupByColumns: [...(src?.groupBy ?? [])],
                sumColumns: [...(src?.sum ?? [])],
                filterColumns: [...(opt.config.report_filters ?? [])],
              }}
              onUpdated={() => { setRestructureId(null); getReports(); }}
              onCreated={(newReportId: string) => {
                setRestructureId(null);
                pendingSelectRef.current = newReportId;
                getReports();
              }}
            />
          );
        })()}

        {/* Restructure report confirmation dialog */}
        <Dialog open={restructureConfirmOpen} onClose={() => setRestructureConfirmOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Restructure Report?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Changing the data source will permanently remove all charts and stat cards you have added to "{
                reportOptions.find((r) => r.id === restructureId)?.label ?? "this report"
              }". This cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setRestructureConfirmOpen(false); setRestructureId(null); }} variant="outlined">
              Cancel
            </Button>
            <Button
              onClick={() => setRestructureConfirmOpen(false)}
              variant="contained"
              color="warning"
            >
              Continue
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete user-generated report confirmation dialog */}
        <Dialog open={!!deleteConfirmId} onClose={() => setDeleteConfirmId(null)} maxWidth="xs" fullWidth>
          <DialogTitle>Delete Report?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              This will permanently delete "
              {reportOptions.find((r) => r.id === deleteConfirmId)?.label ?? "this report"}
              " and all its configuration. This cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteConfirmId(null)} variant="outlined" disabled={deletingReport}>
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              variant="contained"
              color="error"
              disabled={deletingReport}
              startIcon={deletingReport ? <CircularProgress size={14} color="inherit" /> : undefined}
            >
              {deletingReport ? "Deleting…" : "Delete"}
            </Button>
          </DialogActions>
        </Dialog>


        {/* Horizontal Scrollable Nav Bar */}
        <Paper 
          sx={{ 
            mb: 3,
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              bgcolor: theme.palette.mode === "dark" ? "grey.900" : "grey.50",
            }}
          >
            <Tabs
              value={visibleReportOptions.some((r) => r.id === selectedReport) ? selectedReport : false}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                minHeight: 56,
                "& .MuiTab-root": {
                  minHeight: 56,
                  textTransform: "none",
                  fontWeight: 500,
                  fontSize: "0.9rem",
                  px: 3,
                  gap: 1,
                  borderRight: `1px solid ${theme.palette.divider}`,
                  "&:last-of-type": {
                    borderRight: "none",
                  },
                },
                "& .MuiTabs-scrollButtons": {
                  "&.Mui-disabled": {
                    opacity: 0.3,
                  },
                },
              }}
            >
              {visibleReportOptions.map((option) => (
                <Tab
                  key={option.id}
                  value={option.id}
                  label={option.label}
                  icon={option.icon as React.ReactElement}
                  iconPosition="start"
                />
              ))}
            </Tabs>
          </Box>
        </Paper>

        {/* Report Content */}
        <Paper sx={{ p: { xs: 1, sm: 2, md: 3 }, minHeight: 400 }}>
          {renderReportContent()}
        </Paper>
      </Container>
    </ViewContainer>
    </ReportFilterStateProvider>
    </FilterOptionsProvider>
  );
};

export const ReportingView = () => (
  <ReportUserConfigProvider>
    <ReportingViewInner />
  </ReportUserConfigProvider>
);